import apiUrl from '@/apis/api';
import useUserContextStore from '@/contexts/UserContext';
import { postWithAuth, postStreamWithAuth } from '@/lib/httpAdapter';

export interface UnifiedRepository {
  // Repository fields
  name: string;
  repoUUID?: string; // Optional - can be empty for PR bot repos
  summary?: string;
  url: string;
  orgUUID: string;
  userUUID: string;
  isPRBotEnabled: boolean;
  // Per-repo PR bot flags
  enableAutoComments?: boolean;
  enableDocumentation?: boolean;
  enableSecurity?: boolean;
  enableDedup?: boolean;
  /** Effective auto-approve-at-5/5: per-repo override if set, else org default */
  autoApproveOnSafe?: boolean;
  /**
   * Effective agent-insights collection: per-repo override if set, else the org
   * default. Resolved server-side by the same precedence the ingest gates
   * apply — never recompute it here, or the switch stops matching what the
   * pipeline actually does. Unlike the PR-bot flags this is NOT conditional on
   * `hasPRBot`: agent insights cover every connected repo.
   */
  agentInsightsEnabled?: boolean;
  // Dependency fields
  dependencies: string[]; // List of repo names
  isBidirectional: boolean;
  // Status flags
  hasPRBot: boolean;
  hasGeneratedDocs?: boolean;
  /** Bitbucket Cloud — project key from repository listing */
  bitbucketProjectKey?: string;
  /** Bitbucket Cloud — project display name */
  bitbucketProjectName?: string;
}

export interface FetchUnifiedCodebaseManagementResponse {
  repositories: UnifiedRepository[];
  total_count: number;
  has_more: boolean;
  /**
   * Org-level agent-insights default — what every repo with no explicit
   * override inherits. Sent on the listing so the Settings page can render the
   * org-default control without a second request. Defaults to true: an org that
   * has never set it is collecting.
   */
  agent_insights_org_default?: boolean;
  /** Provider pagination: true when provider has another page for this org/workspace scope. */
  provider_has_more?: boolean;
  /** Provider pagination: next provider page number when `provider_has_more` is true. */
  provider_next_page?: number | null;
  /** Provider pagination: next cursor URL (Bitbucket). */
  provider_next_cursor?: string | null;
  /**
   * Dependency graph was truncated (only computed for returned page).
   * UI should avoid making strong claims (e.g. bidirectional links) when true.
   */
  dependencies_truncated?: boolean;
  error?: string;
}

export type FetchUnifiedCodebaseManagementOptions = {
  /** Bitbucket Cloud: restrict to repos in these project keys (server-side filter). */
  bitbucketProjectKeys?: string[];
  /**
   * GitLab: scope listing to this group/subgroup full path (e.g. "my-group/payments").
   * When set, replaces the org-level scope for this request.
   */
  gitlabGroupPath?: string;
  /**
   * Provider pagination cursor (Bitbucket only). When present, backend fetches
   * the next cursor page for the Bitbucket workspace listing.
   */
  providerCursor?: string | null;
  /**
   * Provider pagination page number (GitHub/GitLab only, 1-indexed).
   * When present, FE should keep `offset=0` and page via this field.
   */
  providerPage?: number;
  /**
   * Provider pagination page size (all providers; Bitbucket uses this as `pagelen`).
   * Default: min(limit, 100)
   */
  providerPageSize?: number;
};

export interface ListCodebaseReposOptions {
  limit?: number;
  offset?: number;
  search?: string;
  prBotOnly?: boolean;
  /** PR bot enabled OR has generated TECH_DOCS main doc. */
  prBotOrHasDocs?: boolean;
  /** name | enabled_first | has_dependencies. Always ascending. */
  sortBy?: string;
  /** GitLab: restrict to repos under this group/subgroup path prefix. */
  gitlabGroupPath?: string;
  /**
   * Restrict to repos owned by this git org, provider-neutral.
   *
   * The backend matches an "owner/" prefix, which is what repo names carry on
   * every provider. Sent instead of gitlabGroupPath by GitHub workspaces.
   */
  gitOrg?: string;
}

export interface ListCodebaseReposResponse {
  repositories: UnifiedRepository[];
  total_count: number;
  has_more: boolean;
  /**
   * Org-level agent-insights default — what every repo with no explicit
   * override inherits. Sent on the listing so the Settings page can render the
   * org-default control without a second request. Defaults to true: an org that
   * has never set it is collecting.
   */
  agent_insights_org_default?: boolean;
  error?: string;
}

/**
 * Map backend UnifiedRepository rows (PascalCase) to the FE camelCase shape.
 * Shared by fetchUnifiedCodebaseManagement and listCodebaseRepos — both
 * endpoints return the same row dataclass.
 */
export function mapUnifiedRepositoryRows(
  rows: unknown[]
): UnifiedRepository[] {
  return rows.map((row) => {
    const repo = row as Record<string, unknown>;
    return {
      name: String(repo.Name ?? ''),
      repoUUID: repo.RepoUUID ? String(repo.RepoUUID) : undefined,
      summary: repo.Summary ? String(repo.Summary) : undefined,
      url: String(repo.Url ?? ''),
      orgUUID: String(repo.OrgUUID ?? ''),
      userUUID: String(repo.UserUUID ?? ''),
      isPRBotEnabled: Boolean(repo.IsPRBotEnabled),
      // Use actual flag values when PR bot is installed; all off when no installation
      enableAutoComments: repo.HasPRBot
        ? Boolean(repo.EnableAutoComments)
        : false,
      enableDocumentation: repo.HasPRBot
        ? Boolean(repo.EnableDocumentation)
        : false,
      enableSecurity: repo.HasPRBot ? Boolean(repo.EnableSecurity) : false,
      enableDedup: repo.HasPRBot ? Boolean(repo.EnableDedup) : false,
      autoApproveOnSafe: repo.HasPRBot
        ? Boolean(repo.AutoApproveOnSafe)
        : false,
      // Defaults to true when absent: an older backend that doesn't send the
      // field is still collecting, and rendering the switch off would misreport
      // what the pipeline is doing.
      agentInsightsEnabled: repo.AgentInsightsEnabled !== false,
      dependencies: Array.isArray(repo.Dependencies)
        ? (repo.Dependencies as string[])
        : [],
      isBidirectional: Boolean(repo.IsBidirectional),
      hasPRBot: Boolean(repo.HasPRBot),
      hasGeneratedDocs: Boolean(repo.HasGeneratedDocs),
      bitbucketProjectKey: repo.BitbucketProjectKey
        ? String(repo.BitbucketProjectKey)
        : undefined,
      bitbucketProjectName: repo.BitbucketProjectName
        ? String(repo.BitbucketProjectName)
        : undefined,
    };
  });
}

/**
 * List the org's repos from the backend DB only (POST /listCodebaseRepos/).
 *
 * The DB is the single source of truth for repo listings: instant, complete,
 * identical for every user, zero provider API calls. Real offset pagination.
 * New repos enter the DB via syncCodebaseRepos().
 */
export async function listCodebaseRepos(
  orgUUID: string,
  userUUID: string = '',
  opts: ListCodebaseReposOptions = {}
): Promise<ListCodebaseReposResponse> {
  try {
    const limit =
      Number.isFinite(opts.limit) && (opts.limit ?? 0) > 0 ? opts.limit : 50;
    const offset =
      Number.isFinite(opts.offset) && (opts.offset ?? 0) > 0 ? opts.offset : 0;

    const payload: Record<string, string | number | boolean> = {
      OrgUUID: orgUUID,
      UserUUID: userUUID,
      limit: limit as number,
      offset: offset as number,
      pr_bot_only: opts.prBotOnly ?? false,
      pr_bot_or_has_docs: opts.prBotOrHasDocs ?? false,
      sort_by: opts.sortBy ?? 'name',
    };

    const search = (opts.search ?? '').trim();
    if (search) payload.search = search;

    const gitlabGroupPath = (opts.gitlabGroupPath ?? '').trim();
    if (gitlabGroupPath) payload.gitlab_group_path = gitlabGroupPath;

    const gitOrg = (opts.gitOrg ?? '').trim();
    if (gitOrg) payload.git_org = gitOrg;

    const res = await postWithAuth(`${apiUrl}listCodebaseRepos/`, payload, {
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const raw = await res.json();
    const json = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Record<
      string,
      unknown
    >;

    return {
      repositories: mapUnifiedRepositoryRows(
        Array.isArray(json.Repositories) ? json.Repositories : []
      ),
      total_count: typeof json.total_count === 'number' ? json.total_count : 0,
      has_more: Boolean(json.has_more),
      agent_insights_org_default: json.agent_insights_org_default !== false,
      error:
        json.Error != null
          ? String(json.Error)
          : typeof json.error === 'string'
            ? json.error
            : undefined,
    };
  } catch (error) {
    throw new Error(
      `Failed to list codebase repos: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

export interface SyncCodebaseReposProgress {
  status: 'syncing' | 'done' | 'error';
  total_repos: number;
  added: number;
  /** Live count of repos seen in the provider listing so far — the real
   * progress signal (total_repos barely moves on an already-synced org). */
  scanned?: number;
  error?: string;
}

/**
 * Trigger an explicit provider→DB repo sync (POST /syncCodebaseRepos/, SSE).
 *
 * The backend crawls the org's full provider listing and upserts newly
 * discovered repos into the DB; management surfaces list via listCodebaseRepos().
 * `onProgress` fires for every SSE event (~1/second) so the UI can show live
 * counts. Resolves with the terminal event; rejects on transport failure.
 */
export async function syncCodebaseRepos(
  orgUUID: string,
  userUUID: string = '',
  onProgress?: (event: SyncCodebaseReposProgress) => void
): Promise<SyncCodebaseReposProgress> {
  // Forward the session provider — without it the backend auto-detects and
  // picks wrong for orgs holding integration rows for multiple providers.
  const provider = useUserContextStore.getState().user?.provider ?? 'github';
  const res = await postStreamWithAuth(`${apiUrl}syncCodebaseRepos/`, {
    OrgUUID: orgUUID,
    UserUUID: userUUID,
    provider,
  });
  if (!res.ok || !res.body) {
    throw new Error(`HTTP error! status: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let last: SyncCodebaseReposProgress | null = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by a blank line
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      const line = frame.trim();
      if (!line.startsWith('data: ')) continue;
      try {
        const event = JSON.parse(
          line.slice('data: '.length)
        ) as SyncCodebaseReposProgress;
        last = event;
        onProgress?.(event);
      } catch {
        // skip malformed frame; the terminal event still resolves the call
      }
    }
  }

  if (!last) {
    throw new Error('Sync stream ended without events');
  }
  return last;
}

export async function fetchUnifiedCodebaseManagement(
  orgUUID: string,
  userUUID: string = '',
  limit: number = 50,
  offset: number = 0,
  search: string = '',
  prBotOnly: boolean = false,
  prBotOrHasDocs: boolean = false,
  sortBy: string = 'name',
  order: string = 'asc',
  opts?: FetchUnifiedCodebaseManagementOptions
): Promise<FetchUnifiedCodebaseManagementResponse> {
  try {
    const userCtx = useUserContextStore.getState().user;
    const provider = userCtx?.provider ?? 'github';
    const accessToken = (userCtx?.accessToken ?? '').trim();
    const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 50;
    const safeOffset = Number.isFinite(offset) && offset > 0 ? offset : 0;
    const requestedProviderPageSize =
      typeof opts?.providerPageSize === 'number' &&
      Number.isFinite(opts.providerPageSize)
        ? opts.providerPageSize
        : undefined;
    const providerPageSize = Math.min(
      Math.max(requestedProviderPageSize ?? safeLimit, 1),
      100
    );

    const requestedProviderPage =
      typeof opts?.providerPage === 'number' &&
      Number.isFinite(opts.providerPage)
        ? opts.providerPage
        : undefined;
    const providerPage =
      requestedProviderPage && requestedProviderPage >= 1
        ? requestedProviderPage
        : Math.floor(safeOffset / safeLimit) + 1;
    // Build request payload, omitting empty optional fields
    const payload: Record<string, string | number | boolean | string[]> = {
      OrgUUID: orgUUID,
      UserUUID: userUUID,
      limit: safeLimit,
      offset: safeOffset,
      pr_bot_only: prBotOnly,
      pr_bot_or_has_docs: prBotOrHasDocs,
      provider,
      sort_by: sortBy,
      order,
      provider_page_size: providerPageSize,
      provider_page: providerPage,
    };

    const bbKeys = (opts?.bitbucketProjectKeys ?? [])
      .map((k) => k.trim())
      .filter((k) => k.length > 0);
    if (provider === 'bitbucket' && bbKeys.length > 0) {
      payload.bitbucket_project_keys = bbKeys;
    }

    const gitlabGroupPath = (opts?.gitlabGroupPath ?? '').trim();
    if (provider === 'gitlab' && gitlabGroupPath.length > 0) {
      payload.gitlab_group_path = gitlabGroupPath;
    }

    if (provider === 'bitbucket') {
      const cursor = opts?.providerCursor ?? null;
      if (typeof cursor === 'string' && cursor.trim().length > 0) {
        // Backend expects snake_case field name
        payload.provider_cursor = cursor.trim();
      }
      // Forward the NextAuth Bitbucket OAuth token so the backend can
      // list workspace repos even when the stored integration row has
      // no usable access token. Without this users only see the repos
      // already installed in our DB.
      if (accessToken) {
        payload.access_token = accessToken;
      }
    }

    // Only include search if it's not empty
    if (search && search.trim() !== '') {
      payload.search = search.trim();
    }

    const res = await postWithAuth(
      `${apiUrl}fetchUnifiedCodebaseManagement/`,
      payload,
      {
        credentials: 'include',
      }
    );

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const raw = await res.json();
    const json = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Record<
      string,
      unknown
    >;

    const repositories = mapUnifiedRepositoryRows(
      Array.isArray(json.Repositories) ? json.Repositories : []
    );

    return {
      repositories,
      total_count: typeof json.total_count === 'number' ? json.total_count : 0,
      has_more: Boolean(json.has_more),
      agent_insights_org_default: json.agent_insights_org_default !== false,
      provider_has_more:
        typeof json.provider_has_more === 'boolean'
          ? json.provider_has_more
          : undefined,
      provider_next_page:
        typeof json.provider_next_page === 'number'
          ? json.provider_next_page
          : json.provider_next_page === null
            ? null
            : undefined,
      provider_next_cursor:
        typeof json.provider_next_cursor === 'string'
          ? json.provider_next_cursor
          : json.provider_next_cursor === null
            ? null
            : undefined,
      dependencies_truncated:
        typeof json.dependencies_truncated === 'boolean'
          ? json.dependencies_truncated
          : undefined,
      error:
        json.Error != null && json.Error !== undefined
          ? String(json.Error)
          : typeof json.error === 'string'
            ? json.error
            : undefined,
    };
  } catch (error) {
    throw new Error(
      `Failed to fetch unified codebase management: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
